import { AxisBottom, AxisLeft } from '@visx/axis'
import { curveMonotoneX } from '@visx/curve'
import { localPoint } from '@visx/event'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { useParentSize } from '@visx/responsive'
import { scaleLinear } from '@visx/scale'
import { Area, LinePath } from '@visx/shape'
import { CSSProperties, MouseEvent, TouchEvent, useMemo, useState } from 'react'

import { cn } from '@/lib/utils'

import { MonthRow } from './dashboardData'
import { formatAxisAmount, formatMonthLong, formatMonthShort } from './number-format'

export type ChartMode = 'overview' | 'income' | 'expenses'

/**
 * Barvy sérií. Definujeme je jako vlastní CSS proměnné na kontejneru grafu, takže se hodnota vyhodnotí
 * ve světlém kontextu a tmavý tooltip (třída `dark`) zdědí stejné barvy pro klíče.
 */
const seriesVars = {
  '--series-paid': 'var(--chart-1)',
  '--series-receipts': 'var(--chart-2)',
  '--series-unpaid': 'var(--chart-5)',
  '--series-expenses': 'var(--chart-4)',
  '--series-result': 'var(--chart-3)',
  '--series-prev': 'var(--muted-foreground)',
} as CSSProperties

type SeriesKey = 'income' | 'paid' | 'receipts' | 'unpaid' | 'expenses' | 'result' | 'prev'

const seriesMeta: Record<SeriesKey, { label: string; color: string; shape: 'square' | 'line' | 'dash' }> = {
  income: { label: 'Příjmy', color: 'var(--series-paid)', shape: 'line' },
  paid: { label: 'Zaplacené faktury', color: 'var(--series-paid)', shape: 'square' },
  receipts: { label: 'Pokladna', color: 'var(--series-receipts)', shape: 'square' },
  unpaid: { label: 'Čeká na zaplacení', color: 'var(--series-unpaid)', shape: 'square' },
  expenses: { label: 'Náklady', color: 'var(--series-expenses)', shape: 'line' },
  result: { label: 'Výsledek', color: 'var(--series-result)', shape: 'dash' },
  prev: { label: 'Předchozí rok', color: 'var(--series-prev)', shape: 'dash' },
}

const Swatch = ({ series, className, onDark }: { series: SeriesKey; className?: string; onDark?: boolean }) => {
  const { color, shape } = seriesMeta[series]
  if (shape === 'square') {
    return (
      <span
        className={cn('size-2 shrink-0 rounded-[2px] ring-1 ring-inset', onDark ? 'ring-foreground/45' : 'ring-foreground/15', className)}
        style={{ background: color }}
        aria-hidden
      />
    )
  }
  return (
    <svg width="12" height="8" className={cn('shrink-0', className)} aria-hidden>
      {onDark && <line x1="0" y1="4" x2="12" y2="4" stroke="var(--foreground)" strokeOpacity="0.25" strokeWidth="4" strokeLinecap="round" />}
      <line x1="0" y1="4" x2="12" y2="4" stroke={color} strokeWidth="2" strokeDasharray={shape === 'dash' ? '3 2' : undefined} strokeLinecap="round" />
    </svg>
  )
}

const wholeCzk = new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0, minimumFractionDigits: 0 })
const fmt = (minor: number, signed = false) => {
  const s = wholeCzk.format(Math.abs(minor) / 100)
  if (minor < 0) return `-${s}`
  return signed && minor > 0 ? `+${s}` : s
}

const legendFor = (mode: ChartMode, hasReceipts: boolean, hasPrev: boolean): SeriesKey[] => {
  if (mode === 'overview') return ['income', 'expenses', 'result']
  if (mode === 'expenses') return ['expenses']
  return ['paid', ...(hasReceipts ? (['receipts'] as const) : []), 'unpaid', ...(hasPrev ? (['prev'] as const) : [])]
}

const margin = { top: 12, right: 12, bottom: 36, left: 56 }
const HEIGHT = 288

export const MonthlyGraph = ({ rows, mode, hasReceipts }: { rows: MonthRow[]; mode: ChartMode; hasReceipts: boolean }) => {
  const { parentRef, width } = useParentSize({ debounceTime: 50 })
  const [hover, setHover] = useState<number | null>(null)

  const hasPrev = rows.some((r) => r.paidPrev || r.unpaidPrev)
  const legend = legendFor(mode, hasReceipts, hasPrev)

  const innerW = Math.max(0, width - margin.left - margin.right)
  const innerH = HEIGHT - margin.top - margin.bottom

  const x = useMemo(() => scaleLinear<number>({ domain: [0, Math.max(1, rows.length - 1)], range: [0, innerW] }), [rows.length, innerW])

  const y = useMemo(() => {
    let min = 0
    let max = 0
    for (const r of rows) {
      if (mode === 'overview') {
        max = Math.max(max, r.income, r.expenses, r.result)
        min = Math.min(min, r.result)
      } else if (mode === 'income') {
        max = Math.max(max, r.paid + r.receipts + r.unpaid, hasPrev ? r.paidPrev + r.unpaidPrev : 0)
      } else {
        max = Math.max(max, r.expenses)
      }
    }
    if (max === 0 && min === 0) max = 100000
    return scaleLinear<number>({ domain: [min, max], range: [innerH, 0], nice: 4 })
  }, [rows, mode, innerH, hasPrev])

  // každý druhý popisek, když je málo místa (nikdy se nezalamují ani nepřekrývají)
  const step = innerW / Math.max(1, rows.length - 1) < 34 ? 2 : 1
  const tickValues = rows.map((_, i) => i).filter((i) => (rows.length - 1 - i) % step === 0)

  const onMove = (e: MouseEvent<SVGRectElement> | TouchEvent<SVGRectElement>) => {
    const p = localPoint(e)
    if (!p) return
    const i = Math.round(x.invert(p.x - margin.left))
    setHover(Math.max(0, Math.min(rows.length - 1, i)))
  }

  const xi = (_: MonthRow, i: number) => x(i)
  const stackPaid = (r: MonthRow) => r.paid
  const stackReceipts = (r: MonthRow) => r.paid + r.receipts
  const stackUnpaid = (r: MonthRow) => r.paid + r.receipts + r.unpaid

  const hoverRow = hover !== null ? rows[hover] : null
  const hoverX = hover !== null ? x(hover) : 0
  const flip = hoverX > innerW / 2

  const dots: { key: string; value: number; color: string }[] = []
  if (hoverRow) {
    if (mode === 'overview') {
      dots.push({ key: 'i', value: hoverRow.income, color: seriesMeta.income.color })
      dots.push({ key: 'e', value: hoverRow.expenses, color: seriesMeta.expenses.color })
      dots.push({ key: 'r', value: hoverRow.result, color: seriesMeta.result.color })
    } else if (mode === 'expenses') {
      dots.push({ key: 'e', value: hoverRow.expenses, color: seriesMeta.expenses.color })
    } else {
      dots.push({ key: 'u', value: stackUnpaid(hoverRow), color: 'var(--muted-foreground)' })
      if (hasPrev) dots.push({ key: 'p', value: hoverRow.paidPrev + hoverRow.unpaidPrev, color: seriesMeta.prev.color })
    }
  }

  return (
    <div style={seriesVars}>
      <ul className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        {legend.map((k) => (
          <li key={k} className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <Swatch series={k} />
            {seriesMeta[k].label}
          </li>
        ))}
      </ul>

      <div ref={parentRef} className="relative w-full" style={{ height: HEIGHT }}>
        {width > 0 && (
          <svg width={width} height={HEIGHT} role="img" aria-label="Měsíční vývoj příjmů a nákladů za posledních 13 měsíců" className="block overflow-visible">
            <Group left={margin.left} top={margin.top}>
              <GridRows scale={y} width={innerW} numTicks={4} stroke="var(--border)" strokeWidth={1} />
              {y.domain()[0] < 0 && <line x1={0} x2={innerW} y1={y(0)} y2={y(0)} stroke="var(--muted-foreground)" strokeOpacity={0.5} />}

              {mode === 'overview' && (
                <>
                  <Area data={rows} x={xi} y0={() => y(0)} y1={(r) => y(r.income)} curve={curveMonotoneX} fill="var(--series-paid)" fillOpacity={0.06} />
                  <LinePath data={rows} x={xi} y={(r) => y(r.income)} curve={curveMonotoneX} stroke="var(--series-paid)" strokeWidth={2} />
                  <LinePath data={rows} x={xi} y={(r) => y(r.expenses)} curve={curveMonotoneX} stroke="var(--series-expenses)" strokeWidth={2} />
                  <LinePath data={rows} x={xi} y={(r) => y(r.result)} curve={curveMonotoneX} stroke="var(--series-result)" strokeWidth={1.5} strokeDasharray="4 3" />
                </>
              )}

              {mode === 'income' && (
                <>
                  <Area data={rows} x={xi} y0={(r) => y(stackReceipts(r))} y1={(r) => y(stackUnpaid(r))} curve={curveMonotoneX} fill="var(--series-unpaid)" />
                  {hasReceipts && <Area data={rows} x={xi} y0={(r) => y(stackPaid(r))} y1={(r) => y(stackReceipts(r))} curve={curveMonotoneX} fill="var(--series-receipts)" />}
                  <Area data={rows} x={xi} y0={() => y(0)} y1={(r) => y(stackPaid(r))} curve={curveMonotoneX} fill="var(--series-paid)" fillOpacity={0.85} />
                  {hasPrev && (
                    <LinePath data={rows} x={xi} y={(r) => y(r.paidPrev + r.unpaidPrev)} curve={curveMonotoneX} stroke="var(--series-prev)" strokeWidth={1.5} strokeDasharray="4 3" />
                  )}
                </>
              )}

              {mode === 'expenses' && (
                <>
                  <Area data={rows} x={xi} y0={() => y(0)} y1={(r) => y(r.expenses)} curve={curveMonotoneX} fill="var(--series-expenses)" fillOpacity={0.08} />
                  <LinePath data={rows} x={xi} y={(r) => y(r.expenses)} curve={curveMonotoneX} stroke="var(--series-expenses)" strokeWidth={2} />
                </>
              )}

              <AxisLeft
                scale={y}
                numTicks={4}
                hideAxisLine
                hideTicks
                tickFormat={(v) => formatAxisAmount(Number(v))}
                tickLabelProps={() => ({ fill: 'var(--muted-foreground)', fontSize: 11, textAnchor: 'end', dx: -8, dy: 3, className: 'tabular' })}
              />
              <AxisBottom
                top={innerH}
                scale={x}
                tickValues={tickValues}
                hideAxisLine
                hideTicks
                tickComponent={({ x: tx, y: ty, formattedValue }) => {
                  const d = rows[Number(formattedValue)]?.date
                  if (!d) return null
                  const showYear = d.getMonth() === 0 || Number(formattedValue) === 0
                  return (
                    <text x={tx} y={ty} textAnchor="middle" fontSize={11} fill="var(--muted-foreground)">
                      <tspan x={tx} dy="0.6em">{formatMonthShort(d)}</tspan>
                      {showYear && <tspan x={tx} dy="1.3em" fillOpacity={0.7} className="tabular">{d.getFullYear()}</tspan>}
                    </text>
                  )
                }}
                tickFormat={(v) => String(v)}
              />

              {hoverRow && (
                <g pointerEvents="none">
                  <line x1={hoverX} x2={hoverX} y1={0} y2={innerH} stroke="var(--foreground)" strokeOpacity={0.25} />
                  {dots.map((d) => (
                    <circle key={d.key} cx={hoverX} cy={y(d.value)} r={3.5} fill={d.color} stroke="var(--background)" strokeWidth={1.5} />
                  ))}
                </g>
              )}

              <rect
                width={innerW}
                height={innerH}
                fill="transparent"
                onMouseMove={onMove}
                onTouchStart={onMove}
                onTouchMove={onMove}
                onMouseLeave={() => setHover(null)}
              />
            </Group>
          </svg>
        )}

        {hoverRow && (
          <div
            className="pointer-events-none absolute z-10"
            style={{
              top: margin.top,
              left: margin.left + hoverX,
              transform: flip ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
            }}
          >
            <ChartTooltip row={hoverRow} hasReceipts={hasReceipts} hasPrev={hasPrev} />
          </div>
        )}
      </div>

      {/* tabulka ignoruje šířku 1px ze sr-only a roztahovala stránku na mobilu, proto obal */}
      <div className="sr-only">
      <table>
        <caption>Příjmy a náklady po měsících</caption>
        <thead>
          <tr><th>Měsíc</th><th>Zaplacené faktury</th><th>Pokladna</th><th>Čeká na zaplacení</th><th>Náklady</th><th>Výsledek</th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.date.toISOString()}>
              <td>{formatMonthLong(r.date)}</td><td>{fmt(r.paid)}</td><td>{fmt(r.receipts)}</td><td>{fmt(r.unpaid)}</td><td>{fmt(r.expenses)}</td><td>{fmt(r.result)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

const TooltipRow = ({
  series,
  label,
  value,
  prev,
  strong,
  indent,
}: {
  series?: SeriesKey
  label: string
  value: string
  prev?: string | null
  strong?: boolean
  indent?: boolean
}) => (
  <div className={cn('flex items-baseline gap-2', indent && 'pl-3.5')}>
    {series ? <Swatch series={series} onDark className="self-center" /> : <span className="w-3" />}
    <span className={cn('flex-1 whitespace-nowrap', strong ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
    {prev && <span className="tabular whitespace-nowrap text-[11px] text-muted-foreground">loni {prev}</span>}
    <span className={cn('tabular whitespace-nowrap text-right', strong ? 'font-medium text-foreground' : 'text-foreground')}>{value}</span>
  </div>
)

/** Tmavý tooltip (Mercury): seskupený rozpis měsíce. */
const ChartTooltip = ({ row, hasReceipts, hasPrev }: { row: MonthRow; hasReceipts: boolean; hasPrev: boolean }) => (
  <div className="dark min-w-[248px] rounded-lg border bg-popover px-3 py-2.5 text-xs text-popover-foreground shadow-sm">
    <p className="mb-2 text-[13px] font-medium text-foreground">{formatMonthLong(row.date)}</p>
    <div className="space-y-1.5">
      <TooltipRow series="income" label="Příjmy" value={fmt(row.income)} strong />
      <TooltipRow series="paid" label="Zaplacené faktury" value={fmt(row.paid)} prev={hasPrev ? fmt(row.paidPrev) : null} indent />
      {hasReceipts && <TooltipRow series="receipts" label="Pokladna" value={fmt(row.receipts)} indent />}
      <TooltipRow series="unpaid" label="Čeká na zaplacení" value={fmt(row.unpaid)} prev={hasPrev ? fmt(row.unpaidPrev) : null} />
      <TooltipRow series="expenses" label="Náklady" value={fmt(-row.expenses)} />
    </div>
    <div className="mt-2 flex items-baseline gap-2 border-t pt-2">
      <Swatch series="result" onDark className="self-center" />
      <span className="flex-1 text-foreground">Výsledek</span>
      <span className={cn('tabular text-[13px] font-medium', row.result < 0 ? 'text-danger' : row.result > 0 ? 'text-success' : 'text-foreground')}>
        {fmt(row.result, true)}
      </span>
    </div>
    <p className="mt-1 text-[11px] text-muted-foreground">Zaplacené příjmy minus náklady, bez DPH</p>
  </div>
)
